import { createFileRoute, Link } from "@tanstack/react-router";
import { siteConfig } from "@/config/site";
import { pageMeta, ldScripts, breadcrumbLd, faqLd } from "@/lib/seo";
import { SiteNav } from "@/components/layout/SiteNav";
import { SiteFooter } from "@/components/layout/SiteFooter";
import { EyebrowLabel } from "@/components/ui-ext/Badge";
import { PRICING_TIERS, LOCAL_PRICING } from "@/content/pricing";

const PRICING_FAQS = [
  {
    q: "Why don't you offer flat SEO packages?",
    a: "Because flat packages ignore your actual competitive landscape. An HVAC shop in Austin 78704 faces different technical and authority requirements than an attorney in Dallas. We price based on the work needed to win, not a templated tier.",
  },
  {
    q: "Do you require long-term contracts?",
    a: "Our programs run on a 30-day cancellation clause. We earn the next month's retainer every month by delivering work you can actually see in Search Console and your phone volume.",
  },
  {
    q: "What is included in the monthly retainer?",
    a: "Every program includes technical management, content production, authority building, and reporting. The Focused tier starts at $6,000/mo for flagship metros, while smaller single-city programs can start lower.",
  },
];

export const Route = createFileRoute("/pricing")({
  head: () => ({
    ...pageMeta({
      path: "/pricing/",
      title: `SEO Pricing & Service Tiers | ${siteConfig.name}`,
      description: `Transparent SEO pricing for ${siteConfig.contact.address.city} & Texas businesses. Focused programs from $6K/mo, corridor programs from $3.5K/mo. Book a strategy call.`,
    }),
    scripts: ldScripts(
      breadcrumbLd([
        { name: "Home", path: "/" },
        { name: "Pricing", path: "/pricing/" },
      ]),
      faqLd(PRICING_FAQS),
    ),
  }),
  component: PricingPage,
});

function PricingPage() {
  return (
    <div>
      <SiteNav />
      <main id="main" tabIndex={-1}>
        <section className="container-page pt-24 pb-16">
          <EyebrowLabel>Investment</EyebrowLabel>
          <h1 className="mt-4 font-display text-5xl md:text-7xl leading-tight tracking-tight">
            Transparent pricing.
            <br />
            <span className="text-primary">Measurable growth.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-muted-foreground leading-relaxed">
            We don't hide our numbers. SEO is a compounding investment, and we price it based on the engineering depth required to move your specific needle in your specific market.
          </p>
        </section>

        <section className="container-page pb-24 grid gap-8 lg:grid-cols-3">
          {PRICING_TIERS.map((tier) => (
            <div
              key={tier.name}
              className={`relative rounded-3xl border p-8 flex flex-col ${
                tier.highlight
                  ? "border-primary bg-primary/5 shadow-[var(--shadow-glow)]"
                  : "border-border bg-card/30"
              }`}
            >
              {tier.highlight && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 rounded-full bg-primary px-4 py-1 text-xs font-bold text-primary-foreground uppercase tracking-widest">
                  Most Popular
                </div>
              )}
              <h2 className="font-display text-2xl">{tier.name}</h2>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="text-4xl font-display text-foreground">{tier.price}</span>
                <span className="text-sm text-muted-foreground">{tier.priceNote}</span>
              </div>
              <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
                {tier.description}
              </p>
              <ul className="mt-8 space-y-3 flex-1">
                {tier.features.map((f) => (
                  <li key={f} className="flex gap-3 text-sm text-foreground/90">
                    <span className="text-primary font-bold">·</span>
                    {f}
                  </li>
                ))}
              </ul>
              <Link
                to="/contact/"
                search={{ location: "", service: "seo" }}
                className={`mt-10 w-full rounded-full py-3 text-center text-sm font-semibold transition-transform hover:-translate-y-0.5 ${
                  tier.highlight
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "border border-border-strong hover:border-primary"
                }`}
              >
                {tier.cta}
              </Link>
            </div>
          ))}
        </section>

        <section className="container-page pb-24">
          <div className="rounded-3xl border border-border bg-surface-2 p-8 md:p-12">
            <h2 className="font-display text-3xl">Single-market & Corridor programs</h2>
            <p className="mt-4 max-w-2xl text-muted-foreground">
              For businesses serving smaller Texas metros or specific corridors (like Cedar Park, Georgetown, or the SH-130 growth band), we offer focused programs with lower entry points.
            </p>
            <div className="mt-10 grid gap-8 md:grid-cols-2">
              <div className="rounded-2xl border border-border bg-background/50 p-6">
                <div className="text-xs uppercase tracking-widest text-primary font-semibold">Single location</div>
                <div className="mt-3 text-3xl font-display">{LOCAL_PRICING.singleCity.range}</div>
                <div className="text-sm text-muted-foreground">{LOCAL_PRICING.singleCity.note}</div>
                <p className="mt-3 text-sm text-foreground/85">{LOCAL_PRICING.singleCity.context}</p>
              </div>
              <div className="rounded-2xl border border-border bg-background/50 p-6">
                <div className="text-xs uppercase tracking-widest text-primary font-semibold">Corridor coverage</div>
                <div className="mt-3 text-3xl font-display">{LOCAL_PRICING.corridor.range}</div>
                <div className="text-sm text-muted-foreground">{LOCAL_PRICING.corridor.note}</div>
                <p className="mt-3 text-sm text-foreground/85">{LOCAL_PRICING.corridor.context}</p>
              </div>
            </div>
          </div>
        </section>

        <section className="container-page pb-32">
          <EyebrowLabel>FAQ</EyebrowLabel>
          <h2 className="mt-4 font-display text-3xl md:text-4xl">Pricing questions</h2>
          <dl className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {PRICING_FAQS.map((f) => (
              <div key={f.q} className="rounded-2xl border border-border p-6 bg-card/20">
                <dt className="font-display text-lg tracking-tight">{f.q}</dt>
                <dd className="mt-2 text-sm text-muted-foreground leading-relaxed">{f.a}</dd>
              </div>
            ))}
          </dl>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
